<script setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useUiStore } from '@/stores/ui'

const { t } = useI18n()
const uiStore = useUiStore()

const isOpen = ref(false)

const themeOptions = [
  { value: 'light', label: 'settings.theme_light', icon: '☀️' },
  { value: 'dark', label: 'settings.theme_dark', icon: '🌙' },
  { value: 'system', label: 'settings.theme_system', icon: '💻' }
]

const currentIcon = computed(() => {
  const current = themeOptions.find(o => o.value === uiStore.theme)
  return current?.icon || '☀️'
})

function selectTheme(theme) {
  uiStore.setTheme(theme)
  isOpen.value = false
}

function toggleDropdown() {
  isOpen.value = !isOpen.value
}

function closeDropdown() {
  isOpen.value = false
}
</script>

<template>
  <div class="theme-switcher" v-click-outside="closeDropdown">
    <button class="toggle-btn" @click="toggleDropdown" :title="t('settings.theme')">
      {{ currentIcon }}
    </button>
    
    <Transition name="dropdown">
      <div v-if="isOpen" class="dropdown">
        <button
          v-for="option in themeOptions"
          :key="option.value"
          class="option"
          :class="{ active: uiStore.theme === option.value }"
          @click="selectTheme(option.value)"
        >
          <span class="option-icon">{{ option.icon }}</span>
          <span class="option-label">{{ t(option.label) }}</span>
        </button>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.theme-switcher {
  position: relative;
}

.toggle-btn {
  width: 40px;
  height: 40px;
  border: none;
  background: var(--color-background-secondary);
  border-radius: 8px;
  font-size: 1.25rem;
  cursor: pointer;
  transition: background 0.2s;
}

.toggle-btn:hover {
  background: var(--color-border);
}

.dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  background: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  box-shadow: 0 8px 24px var(--color-shadow);
  padding: 8px;
  min-width: 160px;
  z-index: 100;
}

.option {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 14px;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
  text-align: left;
}

.option:hover {
  background: var(--color-background-secondary);
}

.option.active {
  background: var(--color-primary-light);
}

.option-icon {
  font-size: 1.1rem;
}

.option-label {
  font-size: 0.9rem;
  color: var(--color-text);
}

/* Dropdown transition */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
